import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography, Button, Select, MenuItem, FormControl, Link, Grid, CircularProgress, Radio, RadioGroup, FormControlLabel, Paper, GlobalStyles } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { useAccuracyReport } from './hooks';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import Table from '../../../common/components/Table/Table';
import type { SortConfig, SortDirection } from '../../../common/components/Table/types';
import FileInfoTable from './components/FileInfoTable';
import AccuracyGuidelinesModal from './components/AccuracyGuidelinesModal';
import { 
  MainContent,
  ReportContainer,
  SectionContainer,
  SectionTitle,
  RadioGroupContainer,
  ButtonContainer,
  ActionButton,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton,
  DetailsToggleButton
} from './AccuracyReport.styled';

// Interface for accuracy report item
interface AccuracyReportItem {
  variance: string;
  targetAccuracy: string;
  color: string;
  relBias: string;
  nrReceptacles: string;
  nrSamples: string;
  notionalSampleRate: string;
  estimatedItems: string;
  relStdDev: string;
  xy: string;
  relStdErr: string;
  rh: string;
  bias: string;
  x: string;
  y: string;
  precision: string;
  year: string;
  format: string;
  oeFrom: string;
  oeTo: string;
  accountingType: string;
  mailCategory: string;
  totalNetWeight: string;
  receptacleCategory: string;
  quarter: string;
  xSquare: string;
  ySquare: string;
  accuracy: string;
  ipk: string;
  alertGoodAccuracy: boolean;
  alertNotGoodAccuracy: boolean;
  alertNumberOfSampledReceptacles: boolean;
}

// Interface for the data structure
interface AccuracyReportData {
  priorityItems: AccuracyReportItem[];
  nonPriorityItems: AccuracyReportItem[];
}

interface DirectionOption {
  id: string;
  label: string;
}

// Helper function to format cell values
const formatCellValue = (value: string | number): string => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

// Reusable sorted table wrapper that pins aggregate rows at bottom
const SortedAccuracyTable = React.memo<{
  data: AccuracyReportItem[];
  columns: any[];
  moduleName: string;
  getIsTotalRow?: (row: AccuracyReportItem) => boolean;
  rowBackgroundColor?: (row: AccuracyReportItem) => string;
  rowClassName?: (row: AccuracyReportItem) => string;
}>(
  ({ data, columns, moduleName, getIsTotalRow, rowBackgroundColor, rowClassName }) => {
    const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);

    function parseNumeric(value: unknown): number | null {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const cleaned = value.replace(/[\'`,\s]/g, '');
        const num = Number(cleaned);
        if (Number.isFinite(num)) return num;
      }
      return null;
    }

    const sortedRows = useMemo(() => {
      const rows = data || [];
      if (rows.length === 0) return rows;

      // Detect total/aggregate rows - default heuristic if not provided
      const isTotal = getIsTotalRow
        ? getIsTotalRow
        : (r: AccuracyReportItem) => r.mailCategory === 'ALL' || r.format === 'ALL' || r.year === 'ALL';

      // When no sorting is configured, preserve backend order
      if (!sortConfig) return rows;

      // When sorting is configured, preserve aggregate rows in their original positions
      // and only sort non-aggregate rows within their groups
      const { column, direction } = sortConfig;
      const multiplier = direction === 'ASC' ? 1 : -1;
      
      // Group rows: keep aggregates in place, sort non-aggregates
      const result: AccuracyReportItem[] = [];
      let currentGroup: AccuracyReportItem[] = [];
      
      rows.forEach((row) => {
        if (isTotal(row)) {
          // If we have accumulated non-aggregate rows, sort them and add to result
          if (currentGroup.length > 0) {
            const sortedGroup = [...currentGroup].sort((a: any, b: any) => {
              const av = a?.[column];
              const bv = b?.[column];
              const an = parseNumeric(av);
              const bn = parseNumeric(bv);
              if (av == null && bv == null) return 0;
              if (av == null) return -1 * multiplier;
              if (bv == null) return 1 * multiplier;
              if (an !== null && bn !== null) return (an - bn) * multiplier;
              const astr = String(av);
              const bstr = String(bv);
              return astr.localeCompare(bstr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
            });
            result.push(...sortedGroup);
            currentGroup = [];
          }
          // Add aggregate row in its original position
          result.push(row);
        } else {
          // Accumulate non-aggregate rows for sorting
          currentGroup.push(row);
        }
      });
      
      // Sort and add any remaining non-aggregate rows
      if (currentGroup.length > 0) {
        const sortedGroup = [...currentGroup].sort((a: any, b: any) => {
          const av = a?.[column];
          const bv = b?.[column];
          const an = parseNumeric(av);
          const bn = parseNumeric(bv);
          if (av == null && bv == null) return 0;
          if (av == null) return -1 * multiplier;
          if (bv == null) return 1 * multiplier;
          if (an !== null && bn !== null) return (an - bn) * multiplier;
          const astr = String(av);
          const bstr = String(bv);
          return astr.localeCompare(bstr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
        });
        result.push(...sortedGroup);
      }
      
      return result;
    }, [data, sortConfig, getIsTotalRow]);

    const handleSort = useCallback((column: string, direction: SortDirection | undefined) => {
      if (!direction) { setSortConfig(undefined); return; }
      setSortConfig({ column, direction });
    }, []);

    return (
      <Table
        data={sortedRows}
        columns={columns}
        moduleName={moduleName}
        viewMode={true}
        disableAutoSizing={false}
        pageSize={sortedRows.length}
        showPagination={false}
        enableSearch={false}
        rowBackgroundColor={rowBackgroundColor}
        rowClassName={rowClassName}
        onSort={handleSort}
        maxWidth="100%"
      />
    );
  }
);

// Column definitions for hide details mode
const getHideDetailsColumns = () => [
  {
    Header: 'Format',
    accessor: 'format',
    width: '80px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Mail Category',
    accessor: 'mailCategory',
    width: '120px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Accounting Type',
    accessor: 'accountingType',
    width: '120px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Year',
    accessor: 'year',
    width: '80px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Quarter',
    accessor: 'quarter',
    width: '90px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'OE From',
    accessor: 'oeFrom',
    width: '100px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'OE To',
    accessor: 'oeTo',
    width: '100px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Receptacle Category',
    accessor: 'receptacleCategory',
    width: '140px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Total Net Weight',
    accessor: 'totalNetWeight',
    width: '130px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: '# Receptacles',
    accessor: 'nrReceptacles',
    width: '110px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: '# Samples',
    accessor: 'nrSamples',
    width: '100px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Notional Sample Rate',
    accessor: 'notionalSampleRate',
    width: '150px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'NO2 or NO3 IPK',
    accessor: 'ipk',
    width: '120px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Estimated Items',
    accessor: 'estimatedItems',
    width: '120px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Accuracy (%) (95% confidence)',
    accessor: 'accuracy',
    width: '180px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Target Accuracy',
    accessor: 'targetAccuracy',
    width: '130px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
];

// Column definitions for show details mode
const getShowDetailsColumns = () => [
  {
    Header: 'Format',
    accessor: 'format',
    width: '80px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Mail Category',
    accessor: 'mailCategory',
    width: '120px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Accounting Type',
    accessor: 'accountingType',
    width: '120px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Year',
    accessor: 'year',
    width: '80px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Quarter',
    accessor: 'quarter',
    width: '90px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'OE From',
    accessor: 'oeFrom',
    width: '100px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'OE To',
    accessor: 'oeTo',
    width: '100px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Receptacle Category',
    accessor: 'receptacleCategory',
    width: '140px',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Total Net Weight',
    accessor: 'totalNetWeight',
    width: '130px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: '# Receptacles',
    accessor: 'nrReceptacles',
    width: '110px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: '# Samples',
    accessor: 'nrSamples',
    width: '100px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Notional Sample Rate',
    accessor: 'notionalSampleRate',
    width: '150px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'NO2 or NO3 IPK',
    accessor: 'ipk',
    width: '120px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Estimated Items',
    accessor: 'estimatedItems',
    width: '120px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Rel. std. dev. (%)',
    accessor: 'relStdDev',
    width: '130px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Rel. std. err. (%)',
    accessor: 'relStdErr',
    width: '130px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'x',
    accessor: 'x',
    width: '80px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'y',
    accessor: 'y',
    width: '80px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'xy',
    accessor: 'xy',
    width: '90px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'x^2',
    accessor: 'xSquare',
    width: '90px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'y^2',
    accessor: 'ySquare',
    width: '90px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'rh',
    accessor: 'rh',
    width: '80px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Variance',
    accessor: 'variance',
    width: '100px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Bias',
    accessor: 'bias',
    width: '80px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Rel. Bias (%)',
    accessor: 'relBias',
    width: '110px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Precision (%) (95% confidence)',
    accessor: 'precision',
    width: '180px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Accuracy (%) (95% confidence)',
    accessor: 'accuracy',
    width: '180px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
  {
    Header: 'Target Accuracy',
    accessor: 'targetAccuracy',
    width: '130px',
    textAlign: 'right',
    Cell: ({ value }: { value: string }) => formatCellValue(value),
  },
];

// Utility function to compare year-quarter combinations
const compareYearQuarter = (year1: string, quarter1: string, year2: string, quarter2: string): number => {
  const y1 = parseInt(year1);
  const y2 = parseInt(year2);
  const q1 = parseInt(quarter1.replace('Q', ''));
  const q2 = parseInt(quarter2.replace('Q', ''));
  
  if (y1 !== y2) {
    return y1 - y2; // Compare years first
  }
  return q1 - q2; // If years are equal, compare quarters
};

function AccuracyReport() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [ppoValue, setPpoValue] = useState('');
  const [ppoFilter, setPpoFilter] = useState('Inbound');
  const [fromYear, setFromYear] = useState('');
  const [fromQuarter, setFromQuarter] = useState('');
  const [toYear, setToYear] = useState('');
  const [toQuarter, setToQuarter] = useState('');
  
  // Radio button states
  const [useVersions, setUseVersions] = useState('most_recent');
  const [displayReportFor, setDisplayReportFor] = useState('all_strata');
  const [detailsOption, setDetailsOption] = useState('hide_details');

  // Add state for HTML results
  const [htmlResults, setHtmlResults] = useState<any | null>(null);

  // Guidelines modal state
  const [guidelinesModalOpen, setGuidelinesModalOpen] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success'
  });

  // Accuracy Report hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    error: reportError,
    reset: resetReportError,
  } = useAccuracyReport();

  // Get effective operators and cascading logic for eno1 module
  const {
    operators,
    availableYears,
    operatorsForYearRange,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('eno1', { fromYear, toYear, selectedPpo: ppoValue || undefined });

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = (fromYear || toYear) ? operatorsForYearRange : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYearRange, fromYear, toYear]);

  // Generate year-quarter options with cascading logic
  const yearQuarterOptions = React.useMemo(() => {
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const yearsToShow = ppoValue ? yearsForOperator : availableYears;
    yearsToShow.forEach((year) => {
      quarters.forEach(quarter => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({ value, label, year: year.toString(), quarter });
      });
    });
    return options;
  }, [availableYears, yearsForOperator, ppoValue]);

  // Get current from and to values for display
  const getFromValue = () => {
    if (fromYear && fromQuarter) {
      return `${fromYear}-${fromQuarter}`;
    }
    return '';
  };

  const getToValue = () => {
    if (toYear && toQuarter) {
      return `${toYear}-${toQuarter}`;
    }
    return '';
  };

  // Handle from dropdown change
  const handleFromChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  // Handle to dropdown change
  const handleToChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected to date is earlier than current from date
        if (fromYear && fromQuarter) {
          const comparison = compareYearQuarter(fromYear, fromQuarter, option.year, option.quarter);
          if (comparison > 0) {
            showSnackbar('To date cannot be earlier than From date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Only clear DO selection if the currently selected DO is not available for the new year range
        if (toYear !== option.year && ppoValue) {
          // Check if the current DO is available for the new year range
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has eno1 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'eno1' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newToYearNum = parseInt(option.year);
          
          // Check if the new to year is within the DO's range
          let shouldClearDO = false;
          
          if (fromYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const fromYearNum = parseInt(fromYear);
            shouldClearDO = !(maxYear >= fromYearNum && minYear <= newToYearNum);
          } else {
            // Only to year is selected - check if DO has data up to that year
            shouldClearDO = minYear > newToYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('');
          }
        }
        
        setToYear(option.year);
        setToQuarter(option.quarter);
      }
    } else {
      setToYear('');
      setToQuarter('');
    }
  };

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/eno1');
  };

  const handleDirectionChange = (id: string) => {
    setPpoFilter(id);
  };

  // Handle DO selection change with cascading behavior
  const handleDOChange = (value: string) => {
    // Only clear year selection if the currently selected year range is not available for the new DO
    if (ppoValue !== value && (fromYear || toYear)) {
      // Check if the current year range is available for the new DO
      const relationsForCurrentDO = allRelationsData?.byTargetDO?.[value];
      if (!relationsForCurrentDO) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
        setPpoValue(value);
        return;
      }
      
      // Get years where this DO has eno1 module enabled
      const availableYears = relationsForCurrentDO
        .filter(relation => 
          relation.modules.some(module => 
            module.name === 'eno1' && module.enabled === true
          )
        )
        .map(relation => relation.year)
        .sort((a, b) => a - b);
      
      if (availableYears.length === 0) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
        setPpoValue(value);
        return;
      }
      
      const minYear = availableYears[0];
      const maxYear = availableYears[availableYears.length - 1];
      
      let shouldClearYears = false;
      
      if (fromYear && toYear) {
        // Both from and to are selected - check if the range overlaps with DO's range
        const fromYearNum = parseInt(fromYear);
        const toYearNum = parseInt(toYear);
        shouldClearYears = !(maxYear >= fromYearNum && minYear <= toYearNum);
      } else if (fromYear) {
        // Only from year is selected - check if DO has data from that year onwards
        const fromYearNum = parseInt(fromYear);
        shouldClearYears = maxYear < fromYearNum;
      } else if (toYear) {
        // Only to year is selected - check if DO has data up to that year
        const toYearNum = parseInt(toYear);
        shouldClearYears = minYear > toYearNum;
      }
      
      if (shouldClearYears) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
      }
    }
    setPpoValue(value);
  };

  // Handle from dropdown change with cascading behavior
  const handleFromChangeWithCascading = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Only clear DO selection if the currently selected DO is not available for the new year range
        if (fromYear !== option.year && ppoValue) {
          // Check if the current DO is available for the new year range
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has eno1 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'eno1' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newFromYearNum = parseInt(option.year);
          
          // Check if the new from year is within the DO's range
          let shouldClearDO = false;
          
          if (toYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const toYearNum = parseInt(toYear);
            shouldClearDO = !(maxYear >= newFromYearNum && minYear <= toYearNum);
          } else {
            // Only from year is selected - check if DO has data from that year onwards
            shouldClearDO = maxYear < newFromYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('');
          }
        }
        
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message: string, severity: 'error' | 'warning' | 'info' | 'success' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleReset = () => {
    setPpoValue('');
    setPpoFilter('Inbound');
    setFromYear('');
    setFromQuarter('');
    setToYear('');
    setToQuarter('');
    setUseVersions('most_recent');
    setDisplayReportFor('all_strata');
    setDetailsOption('hide_details');
  };



  // Function to apply current filters to SectionBar (URL params)
  const applyFiltersToSectionBar = () => {
    const newParams = new URLSearchParams();

    // Map PPO filter
    if (ppoValue) {
      newParams.append('ppo', ppoValue);
    }

    // Add year and quarter parameters separately for better URL structure
    if (fromYear && fromQuarter) {
      newParams.append('fromYear', fromYear);
      newParams.append('fromQuarter', fromQuarter);
      newParams.append('fromPeriod', `${fromYear}-${fromQuarter}`);
    }

    if (toYear && toQuarter) {
      newParams.append('toYear', toYear);
      newParams.append('toQuarter', toQuarter);
      newParams.append('toPeriod', `${toYear}-${toQuarter}`);
    }

    newParams.append('useVersions', useVersions);
    newParams.append('displayReportFor', displayReportFor);
    newParams.append('detailsOption', detailsOption);

    setSearchParams(newParams);
  };

  const handleExport = (format: 'HTML' | 'PDF' | 'XLS') => {
    // Reset any previous errors
    resetReportError();

    // Validate required fields
    if (!fromYear || !fromQuarter) {
      showSnackbar('Please select a From period (Year and Quarter)');
      return;
    }

    if (!toYear || !toQuarter) {
      showSnackbar('Please select a To period (Year and Quarter)');
      return;
    }

    // Map display report for values to API format
    const getDisplayForValue = () => {
      switch (displayReportFor) {
        case 'all_strata':
          return 'All Strata';
        case 'mc_and_format':
          return 'MC and Format';
        case 'format':
          return 'Format';
        default:
          return displayReportFor;
      }
    };

    // Map use versions values to API format
    const getAccuracyReportType = () => {
      return useVersions === 'most_recent' ? 'LATEST_VERSION' : 'ORIGINAL_VERSION';
    };

    // Prepare API parameters
    const params = {
      formatType: format.toLowerCase() as 'html' | 'pdf' | 'xls',
      displayFor: getDisplayForValue(),
      showDetails: detailsOption === 'show_details',
      accuracyReportType: getAccuracyReportType() as 'LATEST_VERSION' | 'ORIGINAL_VERSION',
      fromYear: parseInt(fromYear),
      fromQuarter: parseInt(fromQuarter.replace('Q', '')),
      toYear: parseInt(toYear),
      toQuarter: parseInt(toQuarter.replace('Q', '')),
      ...(ppoValue && { designatedOperator: ppoValue }),
      ...(ppoFilter && { table: ppoFilter }),
    };

    generateReport(params, {
      onSuccess: (response) => {
        if (format === 'HTML') {
          // For HTML, display the report data received from the API
          console.log('HTML Report data generated:', response.data);
          console.log('Current filter params used:', params);
          
          if (response.data) {
            setHtmlResults(response.data);
            applyFiltersToSectionBar();
            showSnackbar('HTML report generated successfully', 'success');
          } else {
            showSnackbar('No data available for the selected criteria', 'warning');
          }
        } else {
          // For PDF/XLS, download is handled automatically in the hook
          console.log(`${format} Report downloaded successfully with filename: ${response.filename}`);
          showSnackbar(`${format} report downloaded successfully`, 'success');
        }
      },
      onError: async (error) => {
        console.error('Error generating report:', error);
        
        let errorMessage = 'An error occurred while generating the report';
        let errorStatus: string | undefined;
        
        // Check if it's an axios error
        if (axios.isAxiosError(error)) {
          // For blob responses, the error data might be a Blob containing JSON
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            } catch (parseError) {
              // If parsing fails, use default message
              console.error('Failed to parse error response:', parseError);
            }
          } else if (error.response?.data && typeof error.response.data === 'object') {
            // For non-blob responses, check the data directly
            const errorData = error.response.data as { message?: string; status?: string };
            errorMessage = errorData.message || errorMessage;
            errorStatus = errorData.status;
          }
          
          // Check status code as fallback
          if (!errorStatus && error.response?.status === 404) {
            errorStatus = 'NOT_FOUND';
          }
        }
        
        // Show snackbar with appropriate message for all formats (HTML, PDF, XLS)
        if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No Accuracy reports available')) {
          showSnackbar(
            errorMessage || 'No Accuracy reports available for the selected criteria',
            'warning'
          );
        } else {
          showSnackbar(errorMessage, 'error');
        }
      },
    });
  };

  // Sync URL params with page state when SectionBar filters change (only when results exist)
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');

    // Only sync if we have HTML results (filters are active)
    if (htmlResults) {
      if (ppoParam && ppoParam !== ppoValue) {
        setPpoValue(ppoParam);
      }
    }
  }, [searchParams, htmlResults, ppoValue]);

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setHtmlResults(null);
    // Clear URL parameters when results are cleared
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    if (!htmlResults) return undefined;

    return {
      fromPeriod: getFromValue(),
      toPeriod: getToValue(),
      direction: ppoFilter,
      useVersions,
      displayReportFor,
      detailsOption,
      onFromPeriodChange: (period: string) => {
        if (period) {
          const option = yearQuarterOptions.find(opt => opt.value === period);
          if (option) {
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            
            // Update URL parameters
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('fromYear');
            newParams.delete('fromQuarter');
            newParams.delete('fromPeriod');
            newParams.append('fromYear', option.year);
            newParams.append('fromQuarter', option.quarter);
            newParams.append('fromPeriod', period);
            setSearchParams(newParams);
          }
        } else {
          setFromYear('');
          setFromQuarter('');
          
          // Update URL parameters to clear from period
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('fromYear');
          newParams.delete('fromQuarter');
          newParams.delete('fromPeriod');
          setSearchParams(newParams);
        }
      },
      onToPeriodChange: (period: string) => {
        if (period) {
          const option = yearQuarterOptions.find(opt => opt.value === period);
          if (option) {
            setToYear(option.year);
            setToQuarter(option.quarter);
            
            // Update URL parameters
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('toYear');
            newParams.delete('toQuarter');
            newParams.delete('toPeriod');
            newParams.append('toYear', option.year);
            newParams.append('toQuarter', option.quarter);
            newParams.append('toPeriod', period);
            setSearchParams(newParams);
          }
        } else {
          setToYear('');
          setToQuarter('');
          
          // Update URL parameters to clear to period
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('toYear');
          newParams.delete('toQuarter');
          newParams.delete('toPeriod');
          setSearchParams(newParams);
        }
      },
      onDirectionChange: (direction: string) => {
        setPpoFilter(direction);
        
        // Update URL parameters (note: direction is tracked but not in URL for this report)
        // We keep it in state but don't add to URL to avoid conflicts
      },
      onUseVersionsChange: (version: string) => {
        setUseVersions(version);
      },
      onDisplayReportForChange: (display: string) => {
        setDisplayReportFor(display);
      },
      onDetailsOptionChange: (option: string) => {
        setDetailsOption(option);
      },
      onExport: handleExport,
      isExporting: isGeneratingReport,
    };
  }, [
    htmlResults,
    fromYear,
    fromQuarter,
    toYear,
    toQuarter,
    ppoFilter,
    useVersions,
    displayReportFor,
    detailsOption,
    yearQuarterOptions,
    searchParams,
    setSearchParams,
    handleExport,
    isGeneratingReport,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='accuracy-report-container'>
      <GlobalStyles
        styles={{
          /* Target the 11th column (# Samples) in alert rows */
          '.alert-receptacles td:nth-child(11)': {
            backgroundColor: '#d32f2f !important',
          },
          /* Style the text content within the samples cell */
          '.alert-receptacles td:nth-child(11) [data-testid*="cell-nrSamples"]': {
            color: '#ffffff !important',
            fontWeight: 'bold !important',
          },
          /* Fallback using :has() selector for browsers that support it */
          '.alert-receptacles td:has([data-testid*="cell-nrSamples"])': {
            backgroundColor: '#d32f2f !important',
          },
          
          /* Good Accuracy Alert - Green styling - Only target accuracy column */
          '.alert-good-accuracy td:nth-child(15)': {
            backgroundColor: '#4caf50 !important',
          },
          '.alert-good-accuracy td:nth-child(15) [data-testid*="cell-accuracy"]': {
            color: '#ffffff !important',
            fontWeight: 'bold !important',
          },
          /* Only apply to cells that contain accuracy testid (not relStdDev or relStdErr) */
          '.alert-good-accuracy td:has([data-testid*="cell-accuracy"]):not(:has([data-testid*="cell-relStdDev"])):not(:has([data-testid*="cell-relStdErr"]))': {
            backgroundColor: '#4caf50 !important',
          },
          
          /* Bad Accuracy Alert - Red styling - Only target accuracy column */
          '.alert-bad-accuracy td:nth-child(15)': {
            backgroundColor: '#d32f2f !important',
          },
          '.alert-bad-accuracy td:nth-child(15) [data-testid*="cell-accuracy"]': {
            color: '#ffffff !important',
            fontWeight: 'bold !important',
          },
          /* Only apply to cells that contain accuracy testid (not relStdDev or relStdErr) */
          '.alert-bad-accuracy td:has([data-testid*="cell-accuracy"]):not(:has([data-testid*="cell-relStdDev"])):not(:has([data-testid*="cell-relStdErr"]))': {
            backgroundColor: '#d32f2f !important',
          },
          
          /* For show details mode - target last column (Accuracy) only */
          '.alert-good-accuracy td:last-child [data-testid*="cell-accuracy"]': {
            color: '#ffffff !important',
            fontWeight: 'bold !important',
          },
          '.alert-good-accuracy td:last-child:has([data-testid*="cell-accuracy"]):not(:has([data-testid*="cell-relStdDev"])):not(:has([data-testid*="cell-relStdErr"]))': {
            backgroundColor: '#4caf50 !important',
          },
          
          '.alert-bad-accuracy td:last-child [data-testid*="cell-accuracy"]': {
            color: '#ffffff !important',
            fontWeight: 'bold !important',
          },
          '.alert-bad-accuracy td:last-child:has([data-testid*="cell-accuracy"]):not(:has([data-testid*="cell-relStdDev"])):not(:has([data-testid*="cell-relStdErr"]))': {
            backgroundColor: '#d32f2f !important',
          },
          
          /* Explicitly exclude relStdDev column from alert styling - reset to normal styling */
          '.alert-good-accuracy td:has([data-testid*="cell-relStdDev"]), .alert-bad-accuracy td:has([data-testid*="cell-relStdDev"])': {
            backgroundColor: 'unset !important',
          },
          '.alert-good-accuracy td:has([data-testid*="cell-relStdDev"]) [data-testid*="cell-relStdDev"], .alert-bad-accuracy td:has([data-testid*="cell-relStdDev"]) [data-testid*="cell-relStdDev"]': {
            color: 'inherit !important',
            fontWeight: 'normal !important',
          },
        }}
      />
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='Accuracy Report'
            subtitle="eNO1 Accuracy"
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            showFilters={{
              ppo: !!htmlResults,
              customAccuracy: !!htmlResults,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid="section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="accuracy-report-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='accuracy-report-main-content'>
            <ReportContainer data-testid="accuracy-report-content">
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3 
              }} data-testid="accuracy-report-header">
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#063142' }} data-testid="accuracy-report-title">
                  Accuracy Report
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid="accuracy-report-header-actions">
                  {htmlResults && (
                    <ClearResultsButton 
                      variant="outlined" 
                      onClick={clearResults}
                      data-testid="accuracy-report-clear-results-btn"
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Main Filter Section - Hide when HTML results exist */}
              {!htmlResults && (
                <>
                  {/* Guidelines Button */}
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
                    <Button
                      variant="outlined"
                      onClick={() => setGuidelinesModalOpen(true)}
                      sx={{
                        color: '#063142',
                        borderColor: '#D1D9E0',
                        fontSize: '12px',
                        textTransform: 'none',
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                          borderColor: '#00ACE7',
                          backgroundColor: '#F8F9FA',
                        },
                      }}
                      data-testid="accuracy-report-guidelines-button"
                    >
                      Guidelines for the use and interpretation of the accuracy report
                    </Button>
                  </Box>

                  {/* PPO Section */}
                  <FilterSection data-testid="accuracy-report-filters">
                    {/* PPO Dropdown and Direction Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }} data-testid="accuracy-report-ppo-direction-section">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="accuracy-report-ppo-section">
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          DO
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={ppoValue}
                              onChange={(e) => handleDOChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                              data-testid="accuracy-report-ppo-select"
                            >
                              {isPpoLoading && (
                                <MenuItem value="" disabled>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                </MenuItem>
                              )}
                              {!isPpoLoading && !ppoError && (
                                <MenuItem value="">Select value</MenuItem>
                              )}
                              {!isPpoLoading && !ppoError && ppoOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <span>{option.label}</span>
                                  </Box>
                                </MenuItem>
                              ))}
                              {ppoError && (
                                <MenuItem disabled>Error loading PPOs</MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                      
                      {/* Direction Filter using Segmented Control */}
                      <StyledSegmentedControl>
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={ppoFilter === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{ 
                              color: ppoFilter === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px'
                            }}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      mb: 2 
                    }} />

                    {/* Date Selectors with titles */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          From YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={getFromValue()}
                              onChange={(e) => handleFromChangeWithCascading(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value="">--Please Select--</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          To YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={getToValue()}
                              onChange={(e) => handleToChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value="">--Please Select--</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                    </Box>
                  </FilterSection>

                  {/* Divider */}
                  <Box sx={{ 
                    height: '1px', 
                    backgroundColor: '#E1E8ED', 
                    mb: 2 
                  }} />

                  {/* Main Content Grid - Radio Button sections */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '100%'
                  }}>
                    {/* Use versions */}
                    <Box sx={{ 
                      flex: { xs: 'none', md: '1 1 0' },
                      padding: 2,
                      minWidth: 0
                    }}>
                      <SectionTitle sx={{ mb: 1.5 }}>Use versions:</SectionTitle>
                      <FormControl component="fieldset">
                        <RadioGroupContainer
                          value={useVersions}
                          onChange={(e) => setUseVersions(e.target.value)}
                        >
                          <FormControlLabel 
                            value="most_recent" 
                            control={<Radio />} 
                            label="Most recent eNO1 versions" 
                          />
                          <FormControlLabel 
                            value="original" 
                            control={<Radio />} 
                            label="Original eNO1 versions (versions 0)" 
                          />
                        </RadioGroupContainer>
                      </FormControl>
                    </Box>

                    {/* Vertical Divider */}
                    <Box sx={{ 
                      width: '1px', 
                      backgroundColor: '#E1E8ED',
                      display: { xs: 'none', md: 'block' }
                    }} />

                    {/* Horizontal Divider for mobile */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      my: 2,
                      display: { xs: 'block', md: 'none' }
                    }} />

                    {/* Display accuracy report for */}
                    <Box sx={{ 
                      flex: { xs: 'none', md: '1 1 0' },
                      padding: 2,
                      minWidth: 0
                    }}>
                      <SectionTitle sx={{ mb: 1.5 }}>Display accuracy report for:</SectionTitle>
                      <FormControl component="fieldset">
                        <RadioGroupContainer
                          value={displayReportFor}
                          onChange={(e) => setDisplayReportFor(e.target.value)}
                        >
                          <FormControlLabel 
                            value="all_strata" 
                            control={<Radio />} 
                            label="All Strata" 
                          />
                          <FormControlLabel 
                            value="mc_and_format" 
                            control={<Radio />} 
                            label="MC and Format" 
                          />
                          <FormControlLabel 
                            value="format" 
                            control={<Radio />} 
                            label="Format" 
                          />
                        </RadioGroupContainer>
                      </FormControl>
                    </Box>

                    {/* Vertical Divider */}
                    <Box sx={{ 
                      width: '1px', 
                      backgroundColor: '#E1E8ED',
                      display: { xs: 'none', md: 'block' }
                    }} />

                    {/* Horizontal Divider for mobile */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      my: 2,
                      display: { xs: 'block', md: 'none' }
                    }} />

                    {/* Details options */}
                    <Box sx={{ 
                      flex: { xs: 'none', md: '1 1 0' },
                      padding: 2,
                      minWidth: 0
                    }}>
                      <SectionTitle sx={{ mb: 1.5 }}>Details:</SectionTitle>
                      <FormControl component="fieldset">
                        <RadioGroupContainer
                          value={detailsOption}
                          onChange={(e) => setDetailsOption(e.target.value)}
                        >
                          <FormControlLabel 
                            value="hide_details" 
                            control={<Radio />} 
                            label="Hide details" 
                          />
                          <FormControlLabel 
                            value="show_details" 
                            control={<Radio />} 
                            label="Show details" 
                          />
                        </RadioGroupContainer>
                      </FormControl>
                      
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {htmlResults && (
                        <ClearResultsButton 
                          variant="outlined" 
                          onClick={clearResults}
                          size="small"
                        >
                          Clear Results
                        </ClearResultsButton>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <ResetButton 
                        variant="contained" 
                        onClick={handleReset}
                      >
                        RESET
                      </ResetButton>
                      <ExportButton 
                        variant="contained" 
                        onClick={() => handleExport('HTML')}
                        disabled={isGeneratingReport}
                      >
                        {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                      </ExportButton>
                      <ExportButton 
                        variant="contained" 
                        onClick={() => handleExport('PDF')}
                        disabled={isGeneratingReport}
                      >
                        {isGeneratingReport ? <CircularProgress size={16} /> : 'PDF'}
                      </ExportButton>
                      <ExportButton 
                        variant="contained" 
                        onClick={() => handleExport('XLS')}
                        disabled={isGeneratingReport}
                      >
                        {isGeneratingReport ? <CircularProgress size={16} /> : 'XLS'}
                      </ExportButton>
                    </Box>
                  </ButtonContainer>
                </>
              )}

              {/* Results Section - Show when HTML results exist */}
              {htmlResults && (
                <Box sx={{ mt: 6 }}>
                  {/* File Information Table */}
                  {htmlResults && htmlResults[0] && (htmlResults[0].eno1Files || htmlResults[0].e55Files) && (
                    <FileInfoTable
                      eno1Files={htmlResults[0].eno1Files || []}
                      e55Files={htmlResults[0].e55Files || []}
                    />
                  )}

                  {/* Priority Items Table */}
                  {htmlResults && htmlResults[0] && htmlResults[0].priorityItems && htmlResults[0].priorityItems.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Paper sx={{ overflow: 'hidden' }} data-testid="priority-items-table-container">
                        <SortedAccuracyTable
                          data={htmlResults[0].priorityItems}
                          columns={detailsOption === 'show_details' ? getShowDetailsColumns() : getHideDetailsColumns()}
                          moduleName="Priority Items"
                          rowBackgroundColor={(row: AccuracyReportItem) => {
                            if (row.format === 'ALL') return '#3366ff';
                            if (row.mailCategory === 'ALL') return '#99ccff';
                            if (row.accountingType && row.accountingType.trim() !== '') return '#f5f5f5';
                            return '#ccffff';
                          }}
                          rowClassName={(row: AccuracyReportItem) => {
                            const classes = [] as string[];
                            if (row.mailCategory === 'ALL') classes.push('bold-row');
                            if (row.alertNumberOfSampledReceptacles) classes.push('alert-receptacles');
                            if (row.alertGoodAccuracy) classes.push('alert-good-accuracy');
                            if (row.alertNotGoodAccuracy) classes.push('alert-bad-accuracy');
                            return classes.join(' ');
                          }}
                        />
                      </Paper>
                    </Box>
                  )}

                  {/* Non-Priority Items Table */}
                  {htmlResults && htmlResults[0] && htmlResults[0].nonPriorityItems && htmlResults[0].nonPriorityItems.length > 0 && (
                    <Box>
                      <Paper sx={{ overflow: 'hidden' }} data-testid="non-priority-items-table-container">
                        <SortedAccuracyTable
                          data={htmlResults[0].nonPriorityItems}
                          columns={detailsOption === 'show_details' ? getShowDetailsColumns() : getHideDetailsColumns()}
                          moduleName="Non-Priority Items"
                          rowBackgroundColor={(row: AccuracyReportItem) => {
                            if (row.format === 'ALL') return '#974807';
                            if (row.mailCategory === 'ALL') return '#e46d0a';
                            if (row.accountingType && row.accountingType.trim() !== '') return '#f5f5f5';
                            return '#fcd5b4';
                          }}
                          rowClassName={(row: AccuracyReportItem) => {
                            const classes = [] as string[];
                            if (row.mailCategory === 'ALL') classes.push('bold-row');
                            if (row.alertNumberOfSampledReceptacles) classes.push('alert-receptacles');
                            if (row.alertGoodAccuracy) classes.push('alert-good-accuracy');
                            if (row.alertNotGoodAccuracy) classes.push('alert-bad-accuracy');
                            return classes.join(' ');
                          }}
                        />
                      </Paper>
                    </Box>
                  )}
                </Box>
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid="accuracy-report-snackbar"
      />

      {/* Guidelines Modal */}
      <AccuracyGuidelinesModal
        open={guidelinesModalOpen}
        onClose={() => setGuidelinesModalOpen(false)}
      />
    </Box>
  );
}

export default AccuracyReport; 